<template>
  <div
    ref="scroller"
    class="h-full overflow-y-auto overscroll-contain"
    @scroll="handleScroll"
  >
    <div class="px-4 pb-[calc(env(safe-area-inset-bottom)+24px)]">
      <h1 class="pt-4 pb-3 text-[20px] leading-[26px] text-gray-800">
        <template v-if="total !== undefined">
          {{ formatSearchTotal(total) }}
          {{ total === 1 ? 'registration' : 'registrations' }} for "{{
            store.query
          }}"
        </template>
        <template v-else>Searching…</template>
      </h1>

      <div class="flex items-center gap-2 pb-4">
        <Button
          variant="stroke"
          size="md"
          :icon="FilterIcon"
          @click="handleFilter"
        >
          {{ filterLabel }}
        </Button>
        <Button variant="stroke" size="md" :icon="SortIcon" @click="handleSort">
          Sort
        </Button>
      </div>

      <template v-if="loading">
        <div
          v-for="n in 6"
          :key="n"
          class="flex gap-5 border-b border-[#ebedef] px-2.5 py-[18px]"
        >
          <Skeleton class="size-[68px] shrink-0 rounded-md" />
          <div class="flex-1">
            <Skeleton class="mb-3 h-[15px] w-[180px]" />
            <Skeleton class="mb-2 h-3 w-[140px]" />
            <Skeleton class="h-3 w-[160px]" />
          </div>
        </div>
      </template>
      <template v-else-if="files.length">
        <PublicResultRow v-for="file in files" :key="file.id" :file="file" />
      </template>
      <NoSearchResults v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Button, Skeleton } from '@dacr/library'
import { FilterIcon, SortIcon } from '@dacr/icons'
import {
  FileCategory,
  SearchSort,
  useSearchFilesQuery,
  type SearchFile,
} from '@/shared/graphql/legacy/generated'
import { useMobileSearchStore } from '@/features/search/store'
import { formatSearchTotal } from '@/features/search/formatSearchTotal'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import PublicResultRow from '@/features/search/components/mobile/PublicResultRow.vue'
import NoSearchResults from '@/features/search/components/mobile/NoSearchResults.vue'

const store = useMobileSearchStore()

const scroller = ref<HTMLElement>()

const filterBy = ref<Record<FileCategory, boolean>>({
  [FileCategory.Images]: false,
  [FileCategory.Videos]: false,
  [FileCategory.Audio]: false,
  [FileCategory.Documents]: false,
  [FileCategory.Code]: false,
  [FileCategory.Other]: false,
})
const sortBy = ref<SearchSort>(SearchSort.TimestampDesc)

const selectedCategories = computed(() =>
  (Object.keys(filterBy.value) as FileCategory[]).filter(
    (key) => filterBy.value[key]
  )
)

const filterLabel = computed(() =>
  selectedCategories.value.length
    ? `Filter (${selectedCategories.value.length})`
    : 'Filter'
)

const variables = computed(() => ({
  query: store.query,
  filterBy: selectedCategories.value,
  sortBy: sortBy.value,
}))

const { result, loading, fetchMore } = useSearchFilesQuery(variables)

const total = computed(() => result.value?.searchFiles.total)
const files = computed<SearchFile[]>(
  () => (result.value?.searchFiles.files as SearchFile[] | undefined) ?? []
)

const handleFilter = () => {
  openSheet(Sheets.SearchFilter, {
    props: {
      initialState: { ...filterBy.value },
      onChange: (state: Record<FileCategory, boolean>) => {
        filterBy.value = state
      },
    },
  })
}

const handleSort = () => {
  openSheet(Sheets.SearchSort, {
    props: {
      initialState: sortBy.value,
      onChange: (value: SearchSort) => {
        sortBy.value = value
      },
    },
  })
}

const fetchNextPage = () => {
  const next = result.value?.searchFiles.next
  if (!next || loading.value) return
  fetchMore({
    variables: { next },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!newData) return previousData
      return {
        __typename: 'Query' as const,
        searchFiles: {
          __typename: 'SearchFilesResponse' as const,
          files: [
            ...previousData.searchFiles.files,
            ...newData.searchFiles.files,
          ],
          next: newData.searchFiles.next,
          total: newData.searchFiles.total,
        },
      }
    },
  })
}

const handleScroll = () => {
  const el = scroller.value
  if (!el) return
  if ((el.scrollTop + el.clientHeight) / el.scrollHeight >= 0.8) {
    fetchNextPage()
  }
}
</script>
